import { useCallback, useEffect, useRef, useState } from "react";
import { normalizedPlaqueBounds, analyzePlaqueArtwork, plaqueArtworkGeometry } from "../../utils/plaqueArtworkBounds";

export default function PlaqueArtwork({ artworkUrl, artworkMetadata }) {
  const [measured, setMeasured] = useState(null);
  const [layout, setLayout] = useState(null);
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const current = measured?.url === artworkUrl ? measured : null;
  const inspect = useCallback((image) => {
    const width = image.naturalWidth, height = image.naturalHeight;
    if (!width || !height) return;
    let bounds = artworkMetadata?.normalization?.algorithm === "content-bounds-v3" ?
      normalizedPlaqueBounds(artworkMetadata, width, height) : null;
    let method = "normalization-metadata", analysis = artworkMetadata?.normalization, displayUrl;
    if (!bounds) try {
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      context.drawImage(image, 0, 0);
      const pixels = context.getImageData(0, 0, width, height);
      analysis = analyzePlaqueArtwork(pixels);
      bounds = analysis.bounds; method = analysis.method;
      if (analysis.method === "ink") {
        // Compatibility display only: remove the light matte in memory.
        // The selected file, version and stored pixels remain untouched.
        for (let i = 0; i < width * height; i++) {
          const offset = i * 4;
          const luminance = .2126 * pixels.data[offset] + .7152 * pixels.data[offset + 1] + .0722 * pixels.data[offset + 2];
          pixels.data[offset + 3] = analysis.mask[i] ? Math.max(1, Math.round(pixels.data[offset + 3] * Math.max(0, 1 - luminance / analysis.backgroundLuminance))) : 0;
          pixels.data[offset] = pixels.data[offset + 1] = pixels.data[offset + 2] = 0;
        }
        context.putImageData(pixels, 0, 0);
        displayUrl = canvas.toDataURL("image/png");
      }
    } catch {
      // Pixel access can be unavailable even though an approved image displays.
    }
    if (!bounds) { bounds = normalizedPlaqueBounds(artworkMetadata, width, height); method = "normalization-metadata"; }
    if (!bounds) { bounds = { left: 0, top: 0, width, height }; method = "natural-image"; }
    setMeasured({ url: artworkUrl, bounds, width, height, method, displayUrl,
      selectionReason: analysis?.selectionReason, coverage: Object.fromEntries(Object.entries(analysis || {}).filter(([key]) => key.includes("Coverage"))), alphaBounds: analysis?.alphaBounds, inkBounds: analysis?.inkBounds, analysisWarning: analysis?.reason });
  }, [artworkUrl, artworkMetadata]);
  useEffect(() => {
    if (imageRef.current?.complete && imageRef.current.naturalWidth) inspect(imageRef.current);
  }, [inspect]);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const plaque = wrapper.closest(".vehicle-plaque");
    if (!plaque) return;
    const measure = () => {
      const hero = wrapper.getBoundingClientRect(), plate = plaque.getBoundingClientRect();
      if (hero.width && hero.height && plate.width && plate.height) setLayout({
        hero: { width: hero.width, height: hero.height }, plaque: { width: plate.width, height: plate.height },
      });
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(wrapper); observer?.observe(plaque);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, []);
  const geometry = current && layout ? plaqueArtworkGeometry(current.bounds, layout.plaque, layout.hero) : null;
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && current && layout) {
      const result = plaqueArtworkGeometry(current.bounds, layout.plaque, layout.hero);
      const constraints = (element) => {
        if (!element) return null;
        const css = getComputedStyle(element);
        return Object.fromEntries(["width", "height", "maxWidth", "maxHeight", "transform", "objectFit"].map((key) => [key, css[key]]));
      };
      const wrapper = wrapperRef.current;
      console.debug("Vehicle Plaque artwork geometry", { method: current.method,
        plaque: layout.plaque, hero: layout.hero, source: { width: current.width, height: current.height },
        bounds: current.bounds, selectionReason: current.selectionReason, ...current.coverage, alphaBounds: current.alphaBounds, inkBounds: current.inkBounds, analysisWarning: current.analysisWarning, targetVisibleWidth: result.targetWidth, scale: result.scale,
        visibleWidth: result.visibleWidth, visibleWidthPercent: 100 * result.visibleWidth / layout.plaque.width,
        visibleHeight: result.visibleHeight, constraints: {
          plaque: constraints(wrapper.closest(".vehicle-plaque")),
          hero: constraints(wrapper.parentElement), wrapper: constraints(wrapper),
          viewport: constraints(wrapper.querySelector("svg")), image: constraints(wrapper.querySelector("image")),
        } });
    }
  }, [current, layout]);
  return <div className="plaque-artwork" ref={wrapperRef}>
    {geometry ? <svg role="img" aria-label="Approved vehicle side silhouette"
      viewBox={`0 0 ${layout.hero.width} ${layout.hero.height}`} preserveAspectRatio="xMidYMid meet">
      <image href={current.displayUrl || artworkUrl} x={geometry.x} y={geometry.y}
        width={current.width * geometry.scale} height={current.height * geometry.scale} />
    </svg> : <img ref={imageRef} src={artworkUrl} alt="Approved vehicle side silhouette" onLoad={(event) => inspect(event.currentTarget)} />}
  </div>;
}
